<script setup lang="ts">
import { computed } from 'vue';
import {
  AGENT_NAME_FMEA,
  AGENT_NAME_FORCED_IDEATION,
  AGENT_NAME_INFORMATION_RETRIEVAL,
  AGENT_NAME_MARKET_SIZING,
  AGENT_NAME_PATENT_FTO,
  AGENT_NAME_TECH_EXPLORATION,
  AGENT_TOP_ROUTE_NAME_FMEA,
  AGENT_TOP_ROUTE_NAME_FORCED_IDEATION,
  AGENT_TOP_ROUTE_NAME_INFORMATION_RETRIEVAL,
  AGENT_TOP_ROUTE_NAME_MARKET_SIZING,
  AGENT_TOP_ROUTE_NAME_PATENT_FTO,
  AGENT_TOP_ROUTE_NAME_TECH_EXPLORATION,
} from '@/constants';
import { DgrIcon, DgrPopover } from '@stockmarkteam/donguri-ui';
import Divider from '@/components/common/divider.vue';
import { FunctionKey } from '@/types';
import { useFunctionAccess } from '@/utils/composables/useFunctionAccess';
import { featureFlags } from '@/featureFlags';

// ヘッダーよりも上に表示する
const dropdownZIndex =
  Number(getComputedStyle(document.body).getPropertyValue('--z-header')) + 1;

interface AgentNavigationAction {
  agentName: string;
  routeName: string;
  functionKey: FunctionKey;
  isBeta?: boolean;
}

const AGENT_NAVIGATION_ACTIONS: AgentNavigationAction[] = [
  {
    agentName: AGENT_NAME_INFORMATION_RETRIEVAL,
    routeName: AGENT_TOP_ROUTE_NAME_INFORMATION_RETRIEVAL,
    functionKey: 'information_retrieval',
  },
  {
    agentName: AGENT_NAME_TECH_EXPLORATION,
    routeName: AGENT_TOP_ROUTE_NAME_TECH_EXPLORATION,
    functionKey: 'tech_exploration',
  },
  {
    agentName: AGENT_NAME_PATENT_FTO,
    routeName: AGENT_TOP_ROUTE_NAME_PATENT_FTO,
    functionKey: 'patent_fto',
  },
  // フラグで出し分ける要素には配列の型が伝わらず、functionKey が string になるため as const を付けている。
  // フラグを削除するときに as const も外す。
  ...(featureFlags.ANDEV_9078_FORCED_IDEATION
    ? [
        {
          agentName: AGENT_NAME_FORCED_IDEATION,
          routeName: AGENT_TOP_ROUTE_NAME_FORCED_IDEATION,
          functionKey: 'forced_ideation' as const,
          isBeta: true,
        },
      ]
    : []),
  ...(featureFlags.ANDEV_9479_MARKET_SIZING
    ? [
        {
          agentName: AGENT_NAME_MARKET_SIZING,
          routeName: AGENT_TOP_ROUTE_NAME_MARKET_SIZING,
          functionKey: 'market_sizing' as const,
          isBeta: true,
        },
      ]
    : []),
  ...(featureFlags.ANDEV_10285_FMEA
    ? [
        {
          agentName: AGENT_NAME_FMEA,
          routeName: AGENT_TOP_ROUTE_NAME_FMEA,
          functionKey: 'fmea' as const,
          isBeta: true,
        },
      ]
    : []),
];

const functionAccess = featureFlags.ANDEV_10131_CAPABILITY
  ? useFunctionAccess()
  : undefined;

// 取得中は利用できるか分からないので、全て出したうえで選べない状態にする
const isPending = computed(() => !!functionAccess?.isLoading.value);

const isVisible = (functionKey: FunctionKey) =>
  !functionAccess || isPending.value || functionAccess.canUse(functionKey);

const visibleActions = computed(() =>
  AGENT_NAVIGATION_ACTIONS.filter(action => isVisible(action.functionKey)),
);

// LabsのページとAPIは情報収集の権限で制御されている
const showsLabs = computed(() => isVisible('information_retrieval'));
</script>

<template>
  <DgrPopover
    v-if="visibleActions.length > 0 || showsLabs"
    :z-index="dropdownZIndex"
  >
    <template #default="{ triggerProps }">
      <button
        v-bind="triggerProps"
        type="button"
        class="o-menu-icon-button"
        v-tooltip="{
          content: 'アプリを開く',
          placement: 'bottom-start',
          triggers: ['hover'],
        }"
      >
        <DgrIcon name="menu" :keep-fill="false" class="menu-icon" />
      </button>
    </template>
    <template #content="{ close }">
      <div class="item-container">
        <template
          v-for="(action, index) in visibleActions"
          :key="action.routeName"
        >
          <div
            v-if="isPending"
            class="item disabled c-text-regular--m"
            :class="{ ['last-item']: index === visibleActions.length - 1 }"
            aria-disabled="true"
          >
            {{ action.agentName }}{{ action.isBeta ? ' β' : '' }}
          </div>
          <router-link
            v-else
            :to="{ name: action.routeName }"
            target="_blank"
            @click="close"
            ><div
              class="item c-text-regular--m"
              :class="{
                ['last-item']: index === visibleActions.length - 1,
              }"
            >
              {{ action.agentName }}{{ action.isBeta ? ' β' : '' }}
            </div></router-link
          >
        </template>
        <template v-if="showsLabs">
          <Divider v-if="visibleActions.length > 0" margin="8px 0" />
          <div
            v-if="isPending"
            class="item last-item disabled c-text-regular--m"
            aria-disabled="true"
          >
            Stockmark Labs
          </div>
          <router-link
            v-else
            :to="{ name: 'labs' }"
            target="_blank"
            @click="close"
            ><div class="item last-item c-text-regular--m">
              Stockmark Labs
            </div></router-link
          >
        </template>
      </div>
    </template>
  </DgrPopover>
</template>

<style lang="scss" scoped>
.o-menu-icon-button {
  height: 32px;
  margin-right: 4px;
  padding: 4px;
  border-radius: 4px;
  border: none;
  background-color: $color-white;
  cursor: pointer;
  &:hover {
    background-color: $color-gray200;
  }
  .icon-box.menu-icon {
    color: $color-gray1000;
  }
}

.item-container {
  padding: 12px 0;
  .item {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    width: 100%;
    padding: 4px 12px 4px 16px;
    box-sizing: border-box;
    &:not(.last-item) {
      margin-bottom: 8px;
    }

    &:hover:not(.disabled) {
      background: $color-gray200;
    }

    &.disabled {
      color: $color-gray600;
      cursor: default;
    }

    position: relative;
    &::before {
      content: '';
      position: absolute;
      border-radius: 2px;
      top: 4px;
      bottom: 4px;
      left: 8px;
      width: 4px;
      background: $color-green600;
    }
  }
}
</style>
